import casesService, { INewTag } from "api/casesService";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import CloseIcon from "assets/icons/close.svg?react";
import CourtIcon from "assets/icons/court.svg?react";
import Button from "components/Button";
import Input from "components/Input";
import Loading from "components/Loading";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import SearchLabel from "components/SearchLabel";
import SelectFlagNew from "components/SelectFlagNew";
import StatusBadge from "components/StatusBadge";
import TagBadge from "components/TagBadge";
import CasesContext from "contexts/CasesContext";
import ModalsManagerContext, { CaseModalOptions } from "contexts/ModalsManager";
import SignalsContext from "contexts/SignalsContext";
import {
  CompanyKeyMissingError,
  encryptForCompany,
} from "utils/encryptForCompany";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { IEntity } from "dto/IEntity";
import { IStatus } from "dto/IStatus";
import { ITag, TagType } from "dto/ITag";
import { Formik } from "formik";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { validationSchema } from "./validation.schema";
import CasePerson from "components/CaseParticipantsModal";
import { EEntityType } from "dto/EEntityType";
import { useDebounce } from "use-debounce";
import DropdownInput from "components/DropdownInput";
import { useOnClickOutside } from "hooks/useClickOutside";
import InfoIcon from "assets/icons/info_icon.svg?react";
import CourtPortalCaseInfo from "components/CourtPortalCaseInfo";
import { ICourtPortalCase } from "dto/Cases/ICourtPortalCase";
import { ICase } from "dto/Cases/ICase";
import HollowButton from "components/HollowButton";
import classNames from "classnames";
import { ListType } from "dto/ListType";
import SelectCaseFolders from "components/SelectCaseFolders";
import SearchEntitiesNew from "components/SearchEntitiesNew";
import { randomString } from "utils/randomString";
import { CaseParticipantGroup } from "dto/Cases/CaseParticipantGroup";
import CaseParticipantBadge from "components/CaseParticipantBadge";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import JrwaSignaturePicker, {
  IJrwaSignatureValue,
} from "components/JrwaSignaturePicker";
import jrwaService from "api/jrwaService";
import { useTranslation } from "react-i18next";

interface InitialValues {
  title: string;
  signature: string;
}

const CreateCaseModal: FC<Props> = ({
  isAnimation = true,
  label,
  caseItemToEdit,
  onClose,
  onUpdate,
  onCreate,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [main] = useState<IEntity | null>(null);
  const [caseItem, setCaseItem] = useState<ICaseSimple>(caseItemToEdit);
  const isFullScreen = window.location.href.search("fullscreen") !== -1;

  const { cases, setCases } = useContext(CasesContext);
  const { data: tagsData } = tagsService.useGetTags();
  const tagsList = useMemo(() => tagsData?.data ?? [], [tagsData]);

  // Prywatność jest WYJĄTKIEM, nie domyślnym stanem: sprawa należy do kancelarii i widzi
  // ją cała firma, dopóki autor jawnie jej nie zastrzeże (IsRestricted + CaseAccessGrants).
  const [isPrivate, setIsPrivate] = useState(false);

  const { data: statusesData } = statusService.useGetStatuses();
  const statuses: IStatus[] = useMemo(
    () => statusesData?.data ?? [],
    [statusesData],
  );

  const { modals, setModals, options } = useContext(ModalsManagerContext);
  const { mutate: createCase, isLoading } = casesService.useCreateCase();

  const [status, setStatus] = useState<IStatus | null>(null);
  const [tags, setTags] = useState<ITag[]>([]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showConflictedParticipantsModal, setShowConflictedParticipantsModal] =
    useState(false);
  const [showApprovePICase, setShowApprovePICase] = useState(false);

  const [signature, setSignature] = useState<string>(null);
  const [title, setTitle] = useState<string>(null);

  // Sygnatura wg JRWA (port z Magistratu): nowa teczka dostaje kod klasyfikacyjny
  // i symbol wydziału, a BE nadaje numer ciągły. Ręczna sygnatura zostaje tylko dla
  // firm bez wykazu (brak firmy) i dla edycji teczek bez klasyfikacji (np. sądowych).
  const [jrwa, setJrwa] = useState<IJrwaSignatureValue>({
    classificationId: null,
    departmentSymbol: "",
  });
  const { data: jrwaListData } = jrwaService.useGetList(!caseItemToEdit);
  const jrwaAvailable = (jrwaListData?.data?.Items?.length ?? 0) > 0;
  // BE zasila każdą firmę oficjalnym wykazem, więc JRWA jest „dostępne" zawsze — tryb
  // musi być przełączalny: kancelaria zakłada teczkę z sygnatury sądowej (wyszukiwanie
  // w Portalu Informacyjnym), urząd — z kodu JRWA. Klon z PI przełącza na ręczną.
  const [manualSignature, setManualSignature] = useState(false);
  const useJrwaSignature = !caseItem && jrwaAvailable && !manualSignature;
  const [debouncedSignature] = useDebounce(signature, 300);
  const [debouncedTitle] = useDebounce(title, 300);

  const [folders, setFolders] = useState<string[]>([]);

  const { mutateAsync: setCaseRestricted, isLoading: isRestricting } =
    casesService.useSetCaseRestricted();

  const currentWorkspaceId = getWorkspaceId();
  const { data: courtPortalCasesData } = casesService.useSearchCourtPortalCases(
    {
      signature: debouncedSignature,
    },
    debouncedSignature?.length > 3 && !caseItem,
  );

  const { data: similarCasesData } = casesService.useSearchSimilarCases(
    {
      title: debouncedTitle,
    },
    debouncedTitle?.length > 3 && !caseItem,
  );

  // USUNIĘTE: handleShareCase — przeszyfrowywało klucz sprawy OSOBNO DLA KAŻDEGO członka
  // (mechanizm z ery kluczy profilowych, gdy każdy miał własny klucz). W modelu kluczy
  // firmowych treść jest szyfrowana kluczem FIRMY, który ma każdy członek z grantem, więc
  // rozsyłanie kopii klucza nie dawało dostępu — dawał go dopiero wpis w bazie. Dostęp
  // reguluje dziś widoczność w firmie plus IsRestricted dla spraw prywatnych.

  // const case

  const { data: caseData, isLoading: isDataLoading } = casesService.useGetCase(
    caseItem?.Id ?? "",
    caseItem !== null,
  );

  const [initiailValues, setInitialValues] = useState<InitialValues>({
    title:
      caseData?.Title?.replaceAll("<em>", "").replaceAll("</em>", "") ?? "",
    signature:
      caseData?.Signature?.replaceAll("<em>", "").replaceAll("</em>", "") ?? "",
  });

  const [participants, setParticipants] = useState<ICaseParticipant[]>([]);

  useEffect(() => {
    setParticipants(caseData?.Participants ?? ([] as ICaseParticipant[]));
    setInitialValues({
      title:
        caseData?.Title?.replaceAll("<em>", "").replaceAll("</em>", "") ?? "",
      signature:
        caseData?.Signature?.replaceAll("<em>", "").replaceAll("</em>", "") ??
        "",
    });
    setTags(
      tagsList.filter((t: ITag) => (caseData?.TagIds ?? []).includes(t.Id)),
    );
    if (caseData?.StatusId) {
      const status = statuses.find((s) => s.Id === caseData.StatusId);
      if (status) {
        setStatus(status);
      }
    } else {
      setStatus(null);
    }
  }, [caseData]);

  const { setCallback } = useContext(SignalsContext);

  const handleClickSave = async (values: InitialValues) => {
    const newTags: INewTag[] = tags
      .filter((tag) => tag.Id === "-1")
      .map((tag) => ({
        Id: null,
        Name: tag.Name,
        Type: tag.Type,
      }));

    const tagIds = tags.filter((tag) => tag.Id !== "-1").map((tag) => tag.Id);
    let p: ICaseParticipant[] = participants.map((entity) => {
      let id: string | number = entity.Id ?? "";
      if (entity.ParticipantId) {
        id = entity.ParticipantId;
      }

      if (entity.UserId) {
        id = entity.UserId;
      }

      return {
        Id: entity.Id,
        //@ts-ignore
        UserId:
          entity.Type === EEntityType.EDelivery || entity.Type === 4
            ? entity.Id
            : undefined,
        //@ts-ignore
        ParticipantId:
          entity.Type === EEntityType.NotaParticipant ? id : undefined,
        Email: entity.Email,
        EDeliveryAddress: entity.EDeliveryAddress,
        Address: entity.Address,
        Label: entity.Label,
        RoleId: entity.RoleId ?? null,
        ParcelTypes: entity.ParcelTypes,
        DefaultParcelType: null,
        IsMain: false,
        Group: entity.Group,
      };
    });

    if (main) {
      const part = main;
      let id: string | number = part.Id ?? "";

      if (main.ParticipantId) {
        id = main.ParticipantId;
      }

      if (main.UserId) {
        id = main.UserId;
      }

      p.push({
        Id: part.Id,
        //@ts-ignore
        UserId: part.Type === 1 || part.Type === 4 ? part.Id : undefined,
        //@ts-ignore
        ParticipantId: part.Type === 3 ? id : undefined,
        Email: part.Email,
        EDeliveryAddress: part.EDeliveryAddress,
        Address: part.Address,
        Label: part.Label,
        RoleId: part.RoleId ?? null,
        ParcelTypes: part.ParcelTypes,
        DefaultParcelType: null,
        IsMain: true,
      });
    }

    onUpdate?.(values.title, values.signature, status);

    p = p.map((p) => {
      if (p?.Id?.toString()?.search("TEMP") != -1) {
        p.UserId = undefined;
        p.Id = undefined;
      }

      if (typeof p.Id === "string") {
        p.Id = undefined;
      }
      return p;
    });

    // Dla nowej sprawy generujemy klucz szyfrujący sprawy — klucz prywatny zaszyfrowany
    // kluczem publicznym FIRMY (docs/model-kluczy-firmowych.md, inwariant I1). Dzięki temu
    // sprawę czyta każdy członek firmy z grantem, bez ani jednego udostępnienia.
    let caseKey: { PublicKey: string; PrivateKey: string } | undefined;
    if (!caseItem?.Id) {
      try {
        const generated = await encryptForCompany();
        caseKey = {
          PublicKey: generated.PublicKey,
          PrivateKey: generated.PrivateKey,
        };
      } catch (error) {
        // FAIL-CLOSED: bez klucza firmy NIE zapisujemy sprawy zaszyfrowanej czymkolwiek
        // innym — taka sprawa byłaby nieczytelna dla reszty kancelarii.
        notification({
          title: t("errors.occurred"),
          text:
            error instanceof CompanyKeyMissingError
              ? error.message
              : t("errors.somethingWentWrong"),
          type: ENotificationType.ERROR,
        });
        return;
      }
    }

    if (useJrwaSignature && !jrwa.classificationId) {
      notification({
        title: t("errors.occurred"),
        text: "Wybierz kod klasyfikacyjny JRWA teczki",
        type: ENotificationType.ERROR,
      });
      return;
    }

    createCase(
      {
        Id: caseItem?.Id ?? null,
        NewTags: newTags,
        Participants: p,
        Signature: useJrwaSignature ? undefined : values.signature,
        ClassificationId: useJrwaSignature ? jrwa.classificationId : undefined,
        DepartmentSymbol: useJrwaSignature ? jrwa.departmentSymbol : undefined,
        TagIds: tagIds,
        Title: values.title,
        StatusId: status?.Id as number,
        Folders: folders !== null && folders.length > 0 ? folders : undefined,
        PublicKey: caseKey?.PublicKey,
        PrivateKey: caseKey?.PrivateKey,
      },
      {
        onError: () => {
          notification({
            title: t("errors.occurred"),
            text: t("errors.somethingWentWrong"),
            type: ENotificationType.ERROR,
          });
        },
        onSuccess: (r) => {
          onCreate?.(r.data);
          setCallback(r.data);

          const newCases = [...cases];

          if (caseItem) {
            const toUpdate = newCases.find((c) => c.Id === caseItem.Id);

            if (toUpdate) {
              const clients = participants
                .filter(
                  (f) => f.RoleId === "b25f4943-f576-11ed-aa5c-7acc2dc006f5",
                )
                .map((p) => p.Label ?? "")
                .join(",");

              toUpdate.Title = values.title;
              toUpdate.Signature = values.signature;
              toUpdate.TagIds = r.data.TagIds;
              toUpdate.StatusId = status?.Id;
              toUpdate.Client = clients;
              //   participants.find(
              //     (f) => f.RoleId === 'b25f4943-f576-11ed-aa5c-7acc2dc006f5'
              //   )?.Label ?? '';
              // toUpdate.
              setCases(newCases);
            }
          } else {
            if (isFullScreen) {
              window.open(
                `${
                  import.meta.env.VITE_APP_SITE ??
                  "https://alfa.pocztaprawnicza.pl"
                }/fullscreen/cases/${r.data.Id}/chat`,
              );
            }

            setCases([r.data, ...cases]);
          }

          if (isPrivate && !caseItem?.Id) {
            markCaseAsPrivate(r.data);
          } else {
            handleCloseModal();
          }
        },
      },
    );
  };

  const addNewTag = (tag: ITag | null) => {
    const isTag = tags.find((etag) => etag.Name === tag!.Name);
    if (isTag) return;
    setTags([...tags, tag!]);
  };

  const handleRemoveTag = (tag: ITag) =>
    setTags(tags.filter((etag) => tag.Name !== etag.Name));

  const handleCloseModal = () => {
    if (isAnimation) {
      if (!setCallback) {
        setModals({ ...modals, hide: true });
      } else {
        setModals({ ...modals, createCase: false });
      }
    }
    onClose?.();
    //setCreateCase?.(false);
  };

  useEffect(() => {
    if (options) {
      const o = options as CaseModalOptions;
      if (o.item) {
        setCaseItem(o.item);
      }
    }
    // const item = localStorage.getItem("item");
    // if (!item) return;
    // const parsedItem: ICaseSimple = JSON.parse(item);
    // console.log(parsedItem);
    // SetValues({
    //   ...Values,
    //   Title: parsedItem.Title,
    //   Signature: parsedItem.Signature,
    // });
  }, [options]);

  const [showPerson] = useState(false);
  const [signatureActive, setSignatureActive] = useState(false);
  const [titleActive, setTitleActive] = useState(false);
  const refWrapper = useRef(null);

  const outsideSignatureRef = useRef(null);
  const outsideTitleRef = useRef(null);

  const [showCourtPortalMetric, setShowCourtPortalMetric] = useState(false);
  const [conflictedParticipants, setConflictedParticipant] =
    useState<ICaseParticipant[]>(null);
  const hasParticipantsConflict =
    conflictedParticipants != null && conflictedParticipants.length > 0;
  const [selectedCourtPortalCase, setSelectedCourtPortalCase] =
    useState<ICourtPortalCase>(null);

  useOnClickOutside(outsideSignatureRef, () => {
    if (signatureActive) {
      setSignatureActive(false);
    }
  });

  useOnClickOutside(outsideTitleRef, () => {
    if (titleActive) {
      setTitleActive(false);
    }
  });

  const { mutate: getCourtPortalClone } =
    casesService.useCloneCourtPortalCase();

  const handleCourtPortalClick = (id) => {
    const toSelect = courtPortalCasesData.data?.find(
      (x) => x.ExternalId === Number(id),
    );

    if (toSelect) {
      setSelectedCourtPortalCase(toSelect);
      setShowApprovePICase(true);
    }
  };

  const handleCourtPortalCase = (c: ICourtPortalCase) => {
    setShowApprovePICase(false);
    getCourtPortalClone(
      { id: c.ExternalId },
      {
        onSuccess(data) {
          setSignatureActive(false);
          setParticipants(
            data.data?.Participants ?? ([] as ICaseParticipant[]),
          );
          setInitialValues({
            title: data.data?.Title ?? "",
            signature: data?.data?.Signature ?? "",
          });
          if (data?.data?.Signature) setManualSignature(true);
          const conflicts = (
            data?.data?.Participants ?? ([] as ICaseParticipant[])
          ).filter((x) => x.HasConflict);
          setConflictedParticipant(conflicts);
        },
      },
    );
  };

  const handleShowCourtPortalCaseMetric = (id) => {
    const toSelect = courtPortalCasesData.data?.find(
      (x) => x.ExternalId === Number(id),
    );

    if (toSelect) {
      setSelectedCourtPortalCase(toSelect);
      setShowCourtPortalMetric(true);
    }
  };

  const handleAddEntity = (entity: IEntity) => {
    const payload = {
      Id: randomString(),
      UserId:
        entity.Type === EEntityType.EDelivery ||
        entity.Type === EEntityType.NotaGuest
          ? entity.Id
          : undefined,
      ParticipantId:
        entity.Type === EEntityType.NotaParticipant ? entity.Id : undefined,
      Type: entity.Type,
      Email: entity.Email,
      EDeliveryAddress: entity.EDeliveryAddress,
      Address: entity.Address,
      Label: entity.Label ?? entity.Name,
      RoleId: entity.RoleId ?? null,
      ParcelTypes: entity.ParcelTypes,
      DefaultParcelType: null,
      IsMain: false,
      Group: CaseParticipantGroup.Normal,
    };

    setParticipants([...participants, payload]);
  };

  const handleRemoveParticipant = (p: ICaseParticipant) => {
    setParticipants(participants.filter((x) => x.Id !== p.Id));
  };

  // Wyboru „teczka prywatna" ŚWIADOMIE NIE ZAPAMIĘTUJEMY (poprzednik — „nadaj dostęp
  // wszystkim" — był zapisywany w localStorage). Zapamiętany wybór sprawiłby, że kolejne
  // sprawy powstawałyby prywatne po cichu, a autor dowiadywałby się o tym dopiero z pytania
  // współpracownika, dlaczego czegoś nie widzi. Zastrzeżenie ma być decyzją świadomą.

  const markCaseAsPrivate = async (c: ICase) => {
    try {
      await setCaseRestricted({ caseId: c.Id, isRestricted: true });
    } catch {
      // Sprawa POWSTAŁA — zastrzeżenie się nie udało. Nie przerywamy zamknięcia modala:
      // autor zobaczy sprawę na liście i może ją zastrzec z jej widoku (CaseInfo).
    }

    handleCloseModal();
  };

  if (isDataLoading) {
    return (
      <div className={styles.modal}>
        <Loading />
      </div>
    );
  }

  // const modifyDefaultParcelType = (
  //   entityId: string,
  //   type: { name: string; value: string }
  // ) => {
  //   const newEntities = [...Values.Entities];
  //   const e = newEntities.find((e) => e.Id === entityId);
  //   if (e) {
  //     e.DefaultParcelType = Number(type.value);
  //   }

  //   SetValues({
  //     ...Values,
  //     Entities: newEntities,
  //   });
  // };

  // const modifyAccessToCase = (entityId: string, access: number) => {
  //   const newEntities = [...Values.Entities];
  //   const e = newEntities.find((e) => e.Id === entityId);
  //   if (e) {
  //     console.log("modyfikuje");
  //     e.Privileges = access;
  //   }

  //   SetValues({
  //     ...Values,
  //     Entities: newEntities,
  //   });
  // };

  // const addFromReceivers = (receiver: IReceiver) => {
  //   const element: ICaseParticipant = {
  //     Id: `TEMP_${Math.random()}`,
  //     Label: receiver.Name,
  //     Privileges: 0,
  //     DefaultParcelType: 8,
  //     Email: receiver.Email,
  //     RoleId: null,
  //     IsMain: false,
  //     ParcelTypes: 8,
  //   };

  //   SetValues({
  //     ...Values,
  //     Entities: [...Values.Entities, element].flat(),
  //   });
  // };

  // const removeEntity = (entityId: string) => {
  //   SetValues({
  //     ...Values,
  //     Entities: Values.Entities.filter((e) => e.Id !== entityId),
  //   });
  // };

  return (
    <>
      <ModalLayout
        name="createCase"
        dock={DockPosition.DISABLED}
        off={true}
        customX={8}
        isAnimation={isAnimation}
        customY={0}
        ignore
        refWrapper={refWrapper}
        fullHeight={true}
        disableMove={true}
      >
        <div className={`${styles.wrapwrap} ${showPerson && styles.shadow}`}>
          <Formik
            initialValues={initiailValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={() => {}}
          >
            {({
              values,
              errors,
              setFieldValue,
              setFieldTouched,
              validateForm,
            }) => (
              <>
                <div
                  className={`${styles.wrapper} ${
                    showPerson && styles.disableBorder
                  }`}
                >
                  <div className={styles.title} data-sentry-unmask="true">
                    {label ? label : caseItem ? "Edycja teczki" : "Nowa teczka"}

                    <CloseIcon
                      onClick={handleCloseModal}
                      className={styles.closeIcon}
                    />
                  </div>
                  <div className={styles.content}>
                    <div className={styles.inputs}>
                      <div
                        className={styles.dropdownWrapper}
                        ref={outsideTitleRef}
                      >
                        <Input
                          focus
                          onBlur={() => setFieldTouched("title", true)}
                          name="title"
                          tags={false}
                          placeholder={t("forms.caseTitlePlaceholder")}
                          onChange={(t) => {
                            setFieldValue("title", t);
                            setTitle(t);
                          }}
                          onChooseTag={addNewTag}
                          label={t("forms.title")}
                          value={values.title}
                          isError={errors.title?.length ? errors.title : ""}
                          onFocus={() => setTitleActive(true)}
                        />
                        {titleActive && similarCasesData?.data?.length > 0 && (
                          <DropdownInput
                            title={t("cases.similarCasesFound")}
                            toggle={() => setTitleActive(false)}
                            items={similarCasesData?.data?.map((c) => ({
                              id: c.Id.toString(),
                              title: c.Title,
                              sub: c.Signature,
                            }))}
                            RightIcon={<InfoIcon></InfoIcon>}
                            onClick={() => {}}
                          />
                        )}
                      </div>
                      {useJrwaSignature && (
                        <JrwaSignaturePicker value={jrwa} onChange={setJrwa} />
                      )}
                      {!caseItem && jrwaAvailable && (
                        <button
                          type="button"
                          className={styles.signatureModeLink}
                          onClick={() => setManualSignature((v) => !v)}
                        >
                          {manualSignature
                            ? "Nadaj sygnaturę wg JRWA"
                            : "Wpisz sygnaturę ręcznie (np. sądową)"}
                        </button>
                      )}
                      {!useJrwaSignature && caseData?.ClassificationId && (
                        <Input
                          label={t("cases.caseNumberShort")}
                          name="signature"
                          disabled
                          onChange={() => {}}
                          value={values.signature}
                        />
                      )}
                      <div
                        className={styles.dropdownWrapper}
                        ref={outsideSignatureRef}
                        style={{
                          display:
                            useJrwaSignature || caseData?.ClassificationId
                              ? "none"
                              : undefined,
                        }}
                      >
                        <Input
                          label={t("cases.caseNumberShort")}
                          name="signature"
                          onFocus={() => setSignatureActive(true)}
                          onChange={(t) => {
                            setFieldValue("signature", t);
                            setSignature(t);
                          }}
                          value={values.signature}
                        />
                        {signatureActive &&
                          courtPortalCasesData?.data?.length > 0 && (
                            <DropdownInput
                              toggle={() => setSignatureActive(false)}
                              items={courtPortalCasesData?.data?.map((c) => ({
                                id: c.ExternalId.toString(),
                                title: c.Court,
                                sub: c.Signature,
                              }))}
                              onClick={(id) => handleCourtPortalClick(id)}
                              RightIcon={<InfoIcon></InfoIcon>}
                              onRightClick={(id) =>
                                handleShowCourtPortalCaseMetric(id)
                              }
                            />
                          )}
                        {showCourtPortalMetric && (
                          <ModalLayout refWrapper={outsideSignatureRef}>
                            <CourtPortalCaseInfo
                              toggle={() => setShowCourtPortalMetric(false)}
                              source={selectedCourtPortalCase}
                            ></CourtPortalCaseInfo>
                          </ModalLayout>
                        )}
                      </div>
                    </div>

                    <div className={styles.tags}>
                      <div className={styles.cr}>
                        <SelectFlagNew
                          selectedStatus={null}
                          onChoose={(status) => setStatus(status)}
                          listType={ListType.Cases}
                        />

                        <SearchLabel
                          label={t("forms.label")}
                          type={TagType.Cases}
                          onChoose={addNewTag}
                        />

                        {/* <div
                          onClick={() => setShowParticipantsModal(true)}
                          className={styles.borderButton}
                        >
                          <TeamIcon className={styles.icon} /> Podmioty
                          {participants.length > 0 && (
                            <div className={styles.counter}>
                              {participants.length}
                            </div>
                          )}
                        </div> */}

                        <SearchEntitiesNew
                          onChoose={handleAddEntity}
                          showRoles={true}
                          tags={tagsList}
                        ></SearchEntitiesNew>

                        <SelectCaseFolders
                          folders={folders}
                          setFolders={setFolders}
                          tags={tagsList.filter(
                            (x) => x.Type === TagType.Folder,
                          )}
                        ></SelectCaseFolders>
                      </div>

                      <div className={styles.full}>
                        {status && (
                          <>
                            <StatusBadge
                              status={status}
                              allowRemove
                              showName={true}
                              onRemove={() => setStatus(null)}
                            />
                          </>
                        )}

                        {!!tags.length && (
                          <>
                            {tags.map((tag) => (
                              <TagBadge
                                useBigChip
                                key={tag.Id}
                                tag={tag}
                                onRemove={handleRemoveTag}
                              />
                            ))}
                          </>
                        )}

                        {!!participants.length && (
                          <>
                            {participants.map((p) => (
                              <CaseParticipantBadge
                                key={p.Id}
                                label={p.Label ?? p.Name}
                                onRemove={() => handleRemoveParticipant(p)}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      {hasParticipantsConflict && (
                        <div
                          className={classNames(
                            styles.errorBlock,
                            styles.click,
                          )}
                          onClick={() =>
                            setShowConflictedParticipantsModal(true)
                          }
                        >
                          Istnieje podobny podmiot w Notaup
                        </div>
                      )}
                    </div>
                  </div>

                  {currentWorkspaceId && (
                    <div className={styles.share}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isPrivate}
                              onChange={(e) => setIsPrivate(e.target.checked)}
                            />
                          }
                          label={t("cases.privateCase", {
                            defaultValue: "Teczka prywatna",
                          })}
                        ></FormControlLabel>
                      </FormGroup>
                      {/* Bez tego zdania „prywatna" nie mówi, PRZED KIM — a domyślna
                          widoczność w kancelarii jest zmianą wobec dotychczasowej. */}
                      <div className={styles.privateHint}>
                        {t("cases.privateCaseHint", {
                          defaultValue:
                            "Widoczna tylko dla Ciebie i osób, którym udostępnisz ją imiennie. " +
                            "Bez zaznaczenia teczkę widzi cała kancelaria.",
                        })}
                      </div>
                    </div>
                  )}

                  {/* <div>
                    <Button
                      text="Rzuć błąd"
                      onClick={() => handleThrowError()}
                    ></Button>
                  </div> */}
                  <div className={styles.footer}>
                    <Button
                      loading={isLoading || isRestricting}
                      disabled={!values.title.length}
                      handleError={() => validateForm()}
                      className={styles.button}
                      onClick={() => handleClickSave(values)}
                      text={caseItem ? t("common.save") : t("cases.createNew")}
                    />
                  </div>
                </div>
              </>
            )}
          </Formik>
          {/* {showPerson && (
            <AdditionalClientModal
              showClient={createNewEntity}
              onCreateClient={(entity) => {
                SetValues({
                  ...Values,
                  Entities: [...Values.Entities, entity as ICaseParticipant],
                });
              }}
              isCompany={isCompany}
            />
          )} */}
        </div>
      </ModalLayout>

      {showParticipantsModal && (
        <div className={styles.modal}>
          <div className={styles.bg}></div>
          <div className={styles.modalCenter}>
            <CasePerson
              sourceParticipants={participants}
              toggle={() => setShowParticipantsModal(false)}
              Case={caseData}
              onApply={(v) => {
                setParticipants(v);
                setConflictedParticipant(v.filter((x) => x.HasConflict));
              }}
              showInvitationsColumn={false}
            ></CasePerson>
          </div>
        </div>
      )}

      {showConflictedParticipantsModal && (
        <div className={styles.modal}>
          <div className={styles.bg}></div>
          <div className={styles.modalCenter}>
            <CasePerson
              sourceParticipants={conflictedParticipants}
              toggle={() => setShowConflictedParticipantsModal(false)}
              Case={caseData}
              onApply={(v) => setParticipants(v)}
              conflictsOnly={true}
              showInvitationsColumn={false}
            ></CasePerson>
          </div>
        </div>
      )}

      {showApprovePICase && (
        <div className={styles.modal}>
          <div className={styles.bg}></div>
          <div className={classNames(styles.modalCenter, styles.small)}>
            <div className={styles.iconWrapper}>
              <CourtIcon className={styles.icon}></CourtIcon>
            </div>
            <div className={styles.title}>{t("cases.piCaseSelected")}</div>
            <div className={styles.message}>
              {t("cases.piCaseImportMessage")}
            </div>
            <div className={styles.buttons}>
              <HollowButton
                text={t("common.cancel")}
                onClick={() => setShowApprovePICase(false)}
              ></HollowButton>
              <Button
                text={t("common.approve")}
                onClick={() => handleCourtPortalCase(selectedCourtPortalCase)}
              ></Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateCaseModal;
